import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import Swal from "sweetalert2";

const GoogleMapsAutocomplete = ({ products, onClose }) => {
  const inputRef = useRef(null);
  const [formData, setFormData] = useState({
    address: "", // Address will now include the tagTitle
    city: "",
    state: "",
    postCode: "",
    VenueNotes: "",
  });

  useEffect(() => {
    setFormData({
      address: products?.address || "",
      city: products?.city || "",
      state: products?.state || "",
      postCode: products?.postCode || "",
      VenueNotes: products?.VenueNotes || "",
    });
  }, []);

  // Initialize autocomplete and handle place selection
  useEffect(() => {
    if (inputRef.current) {
      const autocomplete = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          fields: ["formatted_address", "address_components", "name"], // Include 'name' to get the place's name
          strictBounds: false,
        }
      );

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (!place.address_components) {
          alert("No details available for input: '" + place.name + "'");
          return;
        }

        // Extract address components
        const addressComponents = place.address_components;
        let postCode = "",
          state = "",
          city = "",
          address = place.formatted_address;

        addressComponents.forEach((component) => {
          if (component.types.includes("postal_code")) {
            postCode = component.long_name;
          }
          if (component.types.includes("administrative_area_level_1")) {
            state = component.long_name;
          }
          if (component.types.includes("locality")) {
            city = component.long_name;
          }
        });

        // Combine the place's name (tagTitle) with the address
        const fullAddress = place.name ? `${place.name}, ${address}` : address;

        // Update form data with the full address and other details
        setFormData((prevData) => ({
          ...prevData,
          address: fullAddress, // Include tagTitle in the address
          city: city,
          state: state,
          postCode: postCode,
        }));
      });
    }
  }, []);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(
        `https://server-gs.vercel.app/admin/order-venue-edit/${products._id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      const data = await response.json();
      if (response.ok) {
        Swal.fire({
          title: "Success!",
          text: "Order venue updated successfully.",
          icon: "success",
          timer: 1500, // Auto-close after 1.5 seconds
          showConfirmButton: false, // No "OK" button
        });
        onClose(); // Close modal on success
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error("Failed to update order.");
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  return (
    <div className="w-full h-full flex flex-col  items-start  bg-gray-100">
      {/* Location Input */}
      <div className="w-full px-6 pt-6">
        <label className="block font-roboto text-md text-gray-900 mb-2">
          Google map location
        </label>
        <input
          ref={inputRef}
          className="p-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all w-full"
          placeholder="Enter a location"
        />
      </div>

      {/* Address Form */}
      <form onSubmit={handleSubmit} className="w-full">
        <div className="p-6">
          <div className="mb-4">
            <label className="block font-roboto text-md text-gray-900 mb-2">
              Address
            </label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              className="p-3 border border-gray-300 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
          </div>
          <div className="mb-4">
            <label className="block font-roboto text-md text-gray-900 mb-2">
              City
            </label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              className="p-3 border border-gray-300 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
          </div>
          <div className="flex flex-row gap-5 items-center mb-2">
            <div className="mb-4 w-full">
              <label className="block text-sm font-semibold text-gray-700 mb-4">
                State
              </label>
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                className="p-3 border border-gray-300 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div>
            <div className="mb-4">
              <label className="block font-roboto text-md text-gray-900 mb-2">
                Post Code
              </label>
              <input
                type="text"
                name="postCode"
                value={formData.postCode}
                onChange={handleInputChange}
                className="p-3 border border-gray-300 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div>
          </div>
          <div className="mb-4">
            <label className="block font-roboto text-md text-gray-900 mb-2">
              Venue Notes
            </label>
            <input
              type="text"
              name="VenueNotes"
              value={formData.VenueNotes}
              onChange={handleInputChange}
              className="p-3 border border-gray-300 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
          </div>
        </div>
        <div className="p-6 flex justify-start bg-gray-50 border-t border-gray-200">
          <button
            type="submit"
            className="w-56 py-3 font-roboto  bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg focus:ring-4 focus:outline-none focus:ring-blue-300 transition-all"
          >
            Submit
          </button>
        </div>
      </form>
    </div>
  );
};

export default GoogleMapsAutocomplete;
